import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { SajuResult, Element } from '@/utils/sajuCalculations';
import { ELEMENT_COLORS, ELEMENT_BG_COLORS } from '@/utils/sajuCalculations';
import type { LunarConversionInfo } from '@/pages/home/page';

interface Props {
  result: SajuResult;
  solarYear: number;
  solarMonth: number;
  solarDay: number;
  unknownTime: boolean;
  lunarConvInfo: LunarConversionInfo | null;
}

const ELEMENT_EMOJI: Record<Element, string> = {
  목: '🌲', 화: '🔥', 토: '🏔️', 금: '⚡', 수: '💧',
};

const PILLAR_LABEL_EN: Record<string, string> = {
  '년주': 'Year', '월주': 'Month', '일주': 'Day', '시주': 'Hour',
};

const STEMS_EN = ['Jiǎ','Yǐ','Bǐng','Dīng','Wù','Jǐ','Gēng','Xīn','Rén','Guǐ'];
const BRANCHES_EN = ['Zǐ','Chǒu','Yín','Mǎo','Chén','Sì','Wǔ','Wèi','Shēn','Yǒu','Xū','Hài'];

export default function ShareCard({ result, solarYear, solarMonth, solarDay, unknownTime, lunarConvInfo }: Props) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [kakaoDone, setKakaoDone] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const pillars = [
    result.yearPillar,
    result.monthPillar,
    result.dayPillar,
    ...(result.hourPillar && !unknownTime ? [result.hourPillar] : []),
  ];

  const mm = String(solarMonth).padStart(2, '0');
  const dd = String(solarDay).padStart(2, '0');

  const dateStr = isEn
    ? (() => {
        if (lunarConvInfo) {
          const lm = String(lunarConvInfo.lunarMonth).padStart(2, '0');
          const ld = String(lunarConvInfo.lunarDay).padStart(2, '0');
          return `Lunar ${lunarConvInfo.lunarYear}.${lm}.${ld} (Solar ${solarYear}.${mm}.${dd})`;
        }
        return `Solar ${solarYear}.${mm}.${dd}`;
      })()
    : (() => {
        if (lunarConvInfo) {
          const lm = String(lunarConvInfo.lunarMonth).padStart(2, '0');
          const ld = String(lunarConvInfo.lunarDay).padStart(2, '0');
          return `음력 ${lunarConvInfo.lunarYear}.${lm}.${ld} (양력 ${solarYear}.${mm}.${dd})`;
        }
        return `양력 ${solarYear}.${mm}.${dd}`;
      })();

  const pillarText = pillars.map(p => `${p.stem}${p.branch}`).join(' ');

  const shareText = isEn
    ? `✨ BarunSaju Result\nDate of Birth: ${dateStr}\nFour Pillars: ${pillarText}\n\nDay Pillar: ${result.dayPillar.stem}${result.dayPillar.branch} (${STEMS_EN[result.dayPillar.stemIdx]} ${BRANCHES_EN[result.dayPillar.branchIdx]})\n\nCheck your precise Saju at BarunSaju!\n👉 ${window.location.href}`
    : `✨ 바른사주 결과\n생년월일: ${dateStr}\n사주 팔자: ${pillarText}\n\n일주: ${result.dayPillar.stem}${result.dayPillar.branch} (${result.dayPillar.stemKor}${result.dayPillar.branchKor})\n\n바른사주에서 정확한 사주를 확인해보세요!\n👉 ${window.location.href}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleUrlCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = window.location.href;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  };

  const handleKakaoShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('share.kakaoTitle'),
          text: `${pillarText} | ${result.dayPillar.stem}${result.dayPillar.branch}`,
          url: window.location.href,
        });
        setKakaoDone(true);
        setTimeout(() => setKakaoDone(false), 2000);
        return;
      } catch {
        // cancelled or unsupported
      }
    }
    const kakaoText = encodeURIComponent(`✨ ${t('share.kakaoTitle')}\n${pillarText}\n\n${window.location.href}`);
    const kakaoUrl = `https://sharer.kakao.com/talk/friends/picker/easylink?app_key=KAKAO_APP_KEY&shimmer_flag=0&msg=${kakaoText}`;
    window.open(kakaoUrl, '_blank', 'noopener');
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t('share.nativeTitle'),
          text: shareText,
          url: window.location.href,
        });
      } catch {
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  const getPillarLabel = (label: string) => isEn ? (PILLAR_LABEL_EN[label] ?? label) : label;
  const getStemSub = (stemIdx: number, stemKor: string) => isEn ? STEMS_EN[stemIdx] : stemKor;
  const getBranchSub = (branchIdx: number, branchKor: string) => isEn ? BRANCHES_EN[branchIdx] : branchKor;

  const dayPillarSubLabel = isEn
    ? `${STEMS_EN[result.dayPillar.stemIdx]} ${BRANCHES_EN[result.dayPillar.branchIdx]}`
    : `${result.dayPillar.stemKor}${result.dayPillar.branchKor}`;

  return (
    <>
      {/* 공유 버튼 */}
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap mb-4 active:scale-95"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-share-line text-base" />
        </div>
        {t('share.button')}
      </button>

      {/* 공유 모달 */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative bg-white w-full max-w-md rounded-t-3xl pb-10 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* 핸들 */}
            <div className="flex justify-center pt-4 pb-2">
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            {/* 공유 카드 미리보기 */}
            <div
              ref={cardRef}
              className="mx-5 rounded-3xl overflow-hidden mb-5"
              style={{ background: 'linear-gradient(135deg, #1F2937 0%, #374151 50%, #1F2937 100%)' }}
            >
              {/* 카드 헤더 */}
              <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-amber-400 rounded-lg">
                    <i className="ri-sparkling-2-fill text-white text-xs" />
                  </div>
                  <span className="text-white text-sm font-bold">{t('share.appTitle')}</span>
                </div>
                <span className="text-white/40 text-xs">{dateStr}</span>
              </div>

              {/* 4주 8자 */}
              <div className="px-5 pt-4 pb-3">
                <p className="text-white/50 text-xs mb-3 text-center">{t('share.pillarTitle')}</p>
                <div className="flex gap-2 justify-center">
                  {pillars.map(p => (
                    <div key={p.label} className="flex flex-col gap-1.5 items-center">
                      <p className="text-white/40 text-xs">{getPillarLabel(p.label)}</p>
                      <div
                        className="w-14 h-14 rounded-2xl flex flex-col items-center justify-center"
                        style={{ backgroundColor: ELEMENT_BG_COLORS[p.stemElement] }}
                      >
                        <span className="text-xl font-bold" style={{ color: ELEMENT_COLORS[p.stemElement], fontFamily: "'Noto Serif KR', serif" }}>
                          {p.stem}
                        </span>
                        <span className={`font-medium ${isEn ? 'text-[9px]' : 'text-xs'}`} style={{ color: ELEMENT_COLORS[p.stemElement] }}>
                          {getStemSub(p.stemIdx, p.stemKor)}
                        </span>
                      </div>
                      <div
                        className="w-14 h-14 rounded-2xl flex flex-col items-center justify-center"
                        style={{ backgroundColor: ELEMENT_BG_COLORS[p.branchElement] }}
                      >
                        <span className="text-xl font-bold" style={{ color: ELEMENT_COLORS[p.branchElement], fontFamily: "'Noto Serif KR', serif" }}>
                          {p.branch}
                        </span>
                        <span className={`font-medium ${isEn ? 'text-[9px]' : 'text-xs'}`} style={{ color: ELEMENT_COLORS[p.branchElement] }}>
                          {getBranchSub(p.branchIdx, p.branchKor)}
                        </span>
                      </div>
                    </div>
                  ))}
                  {unknownTime && (
                    <div className="flex flex-col gap-1.5 items-center">
                      <p className="text-white/30 text-xs">{t('share.hourPillar')}</p>
                      <div className="w-14 h-14 rounded-2xl border border-dashed border-white/20 flex items-center justify-center">
                        <i className="ri-question-mark text-white/30 text-lg" />
                      </div>
                      <div className="w-14 h-14 rounded-2xl border border-dashed border-white/20 flex items-center justify-center">
                        <i className="ri-question-mark text-white/30 text-lg" />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* 일주 강조 */}
              <div className="mx-5 mb-4 rounded-2xl p-3" style={{ backgroundColor: ELEMENT_BG_COLORS[result.dayPillar.stemElement] + 'CC' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">{t('share.dayPillarLabel')}</p>
                    <p className="text-base font-black" style={{ color: ELEMENT_COLORS[result.dayPillar.stemElement], fontFamily: "'Noto Serif KR', serif" }}>
                      {result.dayPillar.stem}{result.dayPillar.branch}
                      <span className="text-sm font-bold ml-1">({dayPillarSubLabel})</span>
                    </p>
                  </div>
                  <span className="text-2xl">{ELEMENT_EMOJI[result.dayPillar.stemElement]}</span>
                </div>
              </div>

              {/* 카드 푸터 */}
              <div className="px-5 pb-4 text-center">
                <p className="text-white/30 text-xs">{t('share.footer')}</p>
              </div>
            </div>

            {/* SNS 아이콘 버튼들 */}
            <div className="px-5 mb-4">
              <div className="flex items-center gap-3 justify-center">
                {/* 카카오톡 */}
                <button
                  onClick={handleKakaoShare}
                  className="flex flex-col items-center gap-1.5 cursor-pointer"
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all active:scale-90"
                    style={{ backgroundColor: kakaoDone ? '#10B981' : '#FEE500' }}
                  >
                    {kakaoDone
                      ? <i className="ri-check-line text-white text-xl" />
                      : <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 3C7.029 3 3 6.358 3 10.5c0 2.674 1.704 5.023 4.29 6.375l-1.09 4.068c-.095.356.326.644.635.435L11.48 18.9c.17.013.34.02.52.02 4.971 0 9-3.358 9-7.5S16.971 3 12 3Z" fill="#391B1B"/>
                        </svg>
                    }
                  </div>
                  <span className="text-xs text-gray-500 font-medium">{t('share.kakao')}</span>
                </button>

                {/* 링크 복사 */}
                <button
                  onClick={handleUrlCopy}
                  className="flex flex-col items-center gap-1.5 cursor-pointer"
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all active:scale-90"
                    style={{ backgroundColor: urlCopied ? '#10B981' : '#F3F4F6' }}
                  >
                    <i className={`text-xl ${urlCopied ? 'ri-check-line text-white' : 'ri-links-line text-gray-600'}`} />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">{urlCopied ? t('share.copied') : t('share.copyLink')}</span>
                </button>

                {/* 텍스트 복사 */}
                <button
                  onClick={handleCopy}
                  className="flex flex-col items-center gap-1.5 cursor-pointer"
                >
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center transition-all active:scale-90"
                    style={{ backgroundColor: copied ? '#10B981' : '#F3F4F6' }}
                  >
                    <i className={`text-xl ${copied ? 'ri-check-line text-white' : 'ri-file-copy-line text-gray-600'}`} />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">{copied ? t('share.copied') : t('share.copyText')}</span>
                </button>

                {/* 더보기(네이티브 공유) */}
                <button
                  onClick={handleNativeShare}
                  className="flex flex-col items-center gap-1.5 cursor-pointer"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center transition-all active:scale-90">
                    <i className="ri-share-forward-line text-gray-600 text-xl" />
                  </div>
                  <span className="text-xs text-gray-500 font-medium">{t('share.more')}</span>
                </button>
              </div>
            </div>

            {/* 닫기 */}
            <div className="px-5">
              <button
                onClick={() => setOpen(false)}
                className="w-full bg-gray-900 text-white font-bold py-4 rounded-2xl cursor-pointer whitespace-nowrap hover:bg-gray-800 transition-all active:scale-95"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
